import { create } from "zustand";

import { type ArtifactType, type GenerationMode } from "~/shared/data/chat-models";

export interface ChatSettingsValues {
    artifactType: ArtifactType;
    modelId: string | null;
    mode: GenerationMode;
    aspectRatio: string;
    resolution: number;
    duration: number;
    audioEnabled: boolean;
    // Kling Motion Control: character_orientation ("video" / "image");
    // null — у выбранного режима настройки нет (не отправляется).
    characterOrientation: string | null;
    // Topaz Upscale: во сколько раз увеличиваем (2 / 4 / 8); null — у режима
    // такой оси нет и в запрос она не уходит.
    upscaleFactor: number | null;
    batchSize: number;
}

export interface ChatSettingsState extends ChatSettingsValues {
    setArtifactType: (type: ArtifactType) => void;
    setModelId: (id: string | null) => void;
    setMode: (mode: GenerationMode) => void;
    setAspectRatio: (ratio: string) => void;
    setResolution: (res: number) => void;
    setDuration: (seconds: number) => void;
    setAudioEnabled: (on: boolean) => void;
    setCharacterOrientation: (orientation: string | null) => void;
    setUpscaleFactor: (factor: number | null) => void;
    setBatchSize: (n: number) => void;
    applyPreset: (preset: Partial<ChatSettingsValues>) => void;
    reset: () => void;
}

export const INITIAL_CHAT_SETTINGS: ChatSettingsValues = {
    artifactType: "image",
    // Default model is intentionally `nano-banana-pro` (more expensive but
    // currently the only image t2i model with a stable upstream integration
    // on our stand). Backend `/v2/generation/settings` still returns models
    // sorted cheapest-first, so if `nano-banana-pro` ever drops out of the
    // list the existing sync effect will fall back to the cheapest available.
    modelId: "google/nano-banana-pro",
    mode: "text_to_image",
    aspectRatio: "16:9",
    resolution: 1024,
    duration: 0,
    audioEnabled: false,
    characterOrientation: null,
    upscaleFactor: null,
    batchSize: 1,
};

export const useChatSettingsStore = create<ChatSettingsState>((set) => ({
    ...INITIAL_CHAT_SETTINGS,
    setArtifactType: (artifactType) => set({ artifactType }),
    setModelId: (modelId) => set({ modelId }),
    setMode: (mode) => set({ mode }),
    setAspectRatio: (aspectRatio) => set({ aspectRatio }),
    setResolution: (resolution) => set({ resolution }),
    setDuration: (duration) => set({ duration }),
    setAudioEnabled: (audioEnabled) => set({ audioEnabled }),
    setCharacterOrientation: (characterOrientation) => set({ characterOrientation }),
    setUpscaleFactor: (upscaleFactor) => set({ upscaleFactor }),
    setBatchSize: (batchSize) => set({ batchSize }),
    applyPreset: (preset) => set(preset),
    reset: () => set({ ...INITIAL_CHAT_SETTINGS }),
}));
